import React, { useEffect, useMemo, useState } from "react";
import {
  FaSyncAlt,
  FaShoppingCart,
  FaUsers,
  FaDollarSign,
  FaExclamationCircle,
  FaSms,
  FaUniversity,
  FaWallet,
} from "react-icons/fa";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";

import {
  getDashboardSummary,
  getMonthlyCollection,
  getRecentTransactions,
  getChannelSummary,
  getContributionTypeSummary,
  getTopJumuia,
} from "../../services/api";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

export default function DashboardMonitor() {
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(new Date().toLocaleTimeString());

  const [summary, setSummary] = useState({
    totalCollection: 0,
    totalTransactions: 0,
    totalMembers: 0,
    smsSent: 0,
    smsFailed: 0,
    smsBalance:0,
    collectionToday: 0,
    collectionThisMonth: 0,
    cashCollection: 0,
    bankCollection: 0,
    mobileCollection: 0,
    avgContributionPerMember: 0,
  });

  const [monthlyCollection, setMonthlyCollection] = useState([]);
  const [latestTransactions, setLatestTransactions] = useState([]);
  const [channelSummary, setChannelSummary] = useState([]);
  const [typeSummary, setTypeSummary] = useState([]);
  const [topJumuia, setTopJumuia] = useState([]);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setLoading(true);

      const [summaryRes, monthlyRes, recentRes, channelRes, typeRes, jumuiaRes] =
        await Promise.all([
          getDashboardSummary(),
          getMonthlyCollection(),
          getRecentTransactions(),
          getChannelSummary(),
          getContributionTypeSummary(),
          getTopJumuia(),
        ]);

      setSummary(summaryRes.data || {});
      setMonthlyCollection(monthlyRes.data || []);
      setLatestTransactions(recentRes.data || []);
      setChannelSummary(channelRes.data || []);
      setTypeSummary(typeRes.data || []);
      setTopJumuia(jumuiaRes.data || []);
      setLastUpdated(new Date().toLocaleTimeString());
    } catch (err) {
      console.error("Dashboard load error:", err);
    } finally {
      setLoading(false);
    }
  };

  const monthlyChartData = useMemo(() => {
    return {
      labels: monthlyCollection.map((d) => `${d.month_label}`),
      datasets: [
        {
          label: "Collection",
          data: monthlyCollection.map((d) => Number(d.total_amount || 0)),
          backgroundColor: "rgba(59, 130, 246, 0.75)",
          borderRadius: 6,
        },
      ],
    };
  }, [monthlyCollection]);

  const formatMoney = (value) =>
    Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const kpis = [
  {
    title: "Total Collection",
    value: summary.totalCollection || 0,
    icon: <FaDollarSign />,
    color: "#2563EB", // professional blue
    money: true,
  },
  {
    title: "Transactions",
    value: summary.totalTransactions || 0,
    icon: <FaShoppingCart />,
    color: "#4B5563", // cool gray
  },
  {
    title: "Members",
    value: summary.totalMembers || 0,
    icon: <FaUsers />,
    color: "#6B7280", // slightly lighter gray
  },
  {
    title: "Collection Today",
    value: summary.collectionToday || 0,
    icon: <FaWallet />,
    color: "#14B8A6", // teal accent for financial
    money: true,
  },
  {
    title: "This Month",
    value: summary.collectionThisMonth || 0,
    icon: <FaUniversity />,
    color: "#2563EB", // reuse blue for consistency
    money: true,
  },
  {
    title: "SMS Balance",
    value: summary.smsBalance || 0,
    icon: <FaSms />,
    color: "#7C3AED",
  },
  {
    title: "SMS Sent",
    value: summary.smsSent || 0,
    icon: <FaSms />,
    color: "#4B5563", // neutral gray
  },
  {
    title: "SMS Failed",
    value: summary.smsFailed || 0,
    icon: <FaExclamationCircle />,
    color: "#DC2626", // alert red
  },
  // {
  //   title: "Avg / Member",
  //   value: summary.avgContributionPerMember || 0,
  //   icon: <FaDollarSign />,
  //   color: "#10B981", // green for positive metric
  //   money: true,
  // },
];

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div>
          <h1 style={styles.title}>Dashboard Analytics</h1>
          <p style={styles.subtitle}>Church collections, members and activity overview</p>
        </div>

        <div style={styles.headerActions}>
          <span style={styles.lastUpdated}>Updated: {lastUpdated}</span>
          <button style={styles.refreshBtn} onClick={loadDashboard}>
            <FaSyncAlt /> Refresh
          </button>
        </div>
      </header>

      <div style={styles.kpiGrid}>
        {kpis.map((item) => (
          <div key={item.title} style={styles.kpiCard}>
            <div>
              <div style={styles.kpiTitle}>{item.title}</div>
              <div style={styles.kpiValue}>
                {item.money ? `TSH ${formatMoney(item.value)}` : Number(item.value || 0).toLocaleString()}
              </div>
            </div>
            <div style={{ ...styles.kpiIcon, background: item.color }}>{item.icon}</div>
          </div>
        ))}
      </div>

      <div style={styles.sectionGrid}>
        <div style={styles.cardLarge}>
          <h3 style={styles.cardTitle}>Monthly Collection Trend</h3>
          {loading ? (
            <div style={styles.empty}>Loading chart...</div>
          ) : (
            <div style={{ height: 320 }}>
              <Bar
                data={monthlyChartData}
                options={{
                  maintainAspectRatio: false,
                  plugins: { legend: { display: false } },
                  scales: {
                    y: {
                      ticks: {
                        callback: (value) => `TSH ${Number(value).toLocaleString()}`,
                      },
                    },
                  },
                }}
              />
            </div>
          )}
        </div>

        <div style={styles.cardSmall}>
          <h3 style={styles.cardTitle}>Collection by Channel</h3>
          {channelSummary.length === 0 ? (
            <div style={styles.empty}>No data available</div>
          ) : (
            channelSummary.map((c) => (
              <div key={c.channel} style={styles.summaryLine}>
                <span>{c.channel}</span>
                <strong>TSH {formatMoney(c.total_amount)}</strong>
              </div>
            ))
          )}
        </div>
      </div>

      <div style={styles.sectionGrid}>
        <div style={styles.cardLarge}>
          <h3 style={styles.cardTitle}>Latest Transactions</h3>
          {latestTransactions.length === 0 ? (
            <div style={styles.empty}>No transactions available</div>
          ) : (
            <table style={styles.table}>
            <thead style={styles.tableHead}>
              <tr>
                <th style={styles.th}>Trans Code</th>
                <th style={styles.th}>Member</th>
                <th style={styles.th}>Member Code</th>
                <th style={styles.th}>Type</th>
                <th style={{ ...styles.th, textAlign: "right" }}>Amount</th>
                <th style={styles.th}>Date</th>
              </tr>
            </thead>

            <tbody>
              {latestTransactions.map((tx, idx) => (
                <tr
                  key={idx}
                  style={styles.rowHover}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.background = "#f9fafb")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.background = "transparent")
                  }
                >
                  <td style={styles.td}>
                    {tx.transaction_code}
                  </td>

                  <td style={styles.td}>{tx.full_name}</td>

                  <td style={styles.td}>{tx.member_code}</td>

                  <td style={styles.td}>{tx.contribution_type}</td>

                  <td style={{ ...styles.td, ...styles.amountCell }}>
                    TSH {formatMoney(tx.amount)}
                  </td>

                  <td style={{ ...styles.td, ...styles.dateCell }}>
                    {tx.transaction_date
                      ? new Date(tx.transaction_date).toLocaleString()
                      : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>

        <div style={styles.cardSmall}>
          <h3 style={styles.cardTitle}>Top Jumuia</h3>
          {topJumuia.length === 0 ? (
            <div style={styles.empty}>No data available</div>
          ) : (
            topJumuia.map((j) => (
              <div key={j.jumuia_id} style={styles.summaryLine}>
                <span>{j.jumuia_name}</span>
                <strong>TSH {formatMoney(j.total_amount)}</strong>
              </div>
            ))
          )}
        </div>
      </div>

      <div style={styles.cardLarge}>
        <h3 style={styles.cardTitle}>Contribution Type Summary</h3>
        {typeSummary.length === 0 ? (
          <div style={styles.empty}>No contribution summary available</div>
        ) : (
          <table style={styles.table}>
          <thead style={styles.tableHead}>
            <tr>
              <th style={styles.th}>Contribution Type</th>
              <th style={styles.th}>Transactions</th>
              <th style={{ ...styles.th, textAlign: "right" }}>Total Amount</th>
            </tr>
          </thead>

          <tbody>
            {typeSummary.map((row, idx) => (
              <tr
                key={idx}
                style={styles.rowHover}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#f9fafb")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <td style={styles.td}>{row.contribution_type}</td>

                <td style={styles.td}>
                  {Number(row.txCount || 0).toLocaleString()}
                </td>

                <td style={{ ...styles.td, ...styles.amountCell }}>
                  TSH {formatMoney(row.total)}
                </td>
              </tr>
            ))}
          </tbody>
</table>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding: "20px",
    background: "#f8fafc",
    minHeight: "100vh",
    fontFamily: "Inter, sans-serif",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "20px",
  },
  title: {
    margin: 0,
    fontSize: "24px",
    fontWeight: 700,
    color: "#111827",
  },
  subtitle: {
    margin: "4px 0 0",
    color: "#6b7280",
    fontSize: "14px",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  lastUpdated: {
    fontSize: "12px",
    color: "#6b7280",
  },
  refreshBtn: {
    background: "#111827",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "10px 14px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    fontWeight: 600,
  },
  kpiGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "16px",
    marginBottom: "20px",
  },
  kpiCard: {
    background: "#fff",
    borderRadius: "14px",
    padding: "18px",
    boxShadow: "0 6px 20px rgba(15, 23, 42, 0.06)",
    border: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  kpiTitle: {
    color: "#6b7280",
    fontSize: "13px",
    marginBottom: "8px",
    fontWeight: 600,
  },
  kpiValue: {
    color: "#111827",
    fontSize: "20px",
    fontWeight: 700,
  },
  kpiIcon: {
    width: "42px",
    height: "42px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    fontSize: "18px",
  },
  sectionGrid: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr",
    gap: "16px",
    marginBottom: "20px",
  },
  cardLarge: {
    background: "#fff",
    borderRadius: "14px",
    padding: "18px",
    boxShadow: "0 6px 20px rgba(15, 23, 42, 0.06)",
    border: "1px solid #e5e7eb",
  },
  cardSmall: {
    background: "#fff",
    borderRadius: "14px",
    padding: "18px",
    boxShadow: "0 6px 20px rgba(15, 23, 42, 0.06)",
    border: "1px solid #e5e7eb",
  },
  cardTitle: {
    marginTop: 0,
    marginBottom: "14px",
    color: "#111827",
    fontSize: "16px",
    fontWeight: 700,
  },
  summaryLine: {
    display: "flex",
    justifyContent: "space-between",
    padding: "10px 0",
    borderBottom: "1px solid #f1f5f9",
    fontSize: "14px",
    color: "#374151",
  },
  empty: {
    padding: "18px",
    borderRadius: "8px",
    background: "#f8fafc",
    color: "#6b7280",
    textAlign: "center",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px",
  },

  table: {
  width: "100%",
  borderCollapse: "separate",
  borderSpacing: 0,
  fontSize: "13px",
  background: "#fff",
},
tableHead: {
  background: "#f9fafb",
},
th: {
  textAlign: "left",
  padding: "12px 14px",
  fontSize: "12px",
  fontWeight: 700,
  color: "#374151",
  borderBottom: "1px solid #e5e7eb",
  letterSpacing: "0.03em",
  textTransform: "uppercase",
  whiteSpace: "nowrap",
},
td: {
  padding: "12px 14px",
  borderBottom: "1px solid #f1f5f9",
  color: "#111827",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
},
rowHover: {
  cursor: "pointer",
  transition: "background 0.2s ease",
},
amountCell: {
  textAlign: "right",
  fontWeight: 600,
},
dateCell: {
  color: "#6b7280",
  fontSize: "12px",
  whiteSpace: "nowrap",
},
};